import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ResponsiveModal from "@/components/ResponsiveModal";
import SupportFeedbackDialog from "@/components/SupportFeedbackDialog";
import { CheckCircle2, Sparkles, Infinity as InfinityIcon, Info, LifeBuoy, Lock, CreditCard, RefreshCw, XCircle } from "lucide-react";

const PLAN_BADGE = {
  trial: "Start here · free",
  monthly: "Most popular",
  yearly: "Best value",
  lifetime: "Pay once · keep forever",
};

const PLAN_ORDER = ["trial", "monthly", "yearly", "lifetime"];

const FEATURES = [
  "Personalized, condition-aware nutrition",
  "Condition-aware, clinically guided meal planning",
  "Macro, calorie, fiber & micronutrient tracking",
  "Multi-agentic AI personalization for your biology",
  "Insurance Management — upload & track your health cover (coming soon)",
  "Evidence-informed nutrition for PCOS, T2D, fatty liver & more",
];

const detectCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (/Kolkata|Calcutta/i.test(tz)) return "INR";
    if ((navigator.language || "").toLowerCase().endsWith("-in")) return "INR";
    return "USD";
  } catch (_) { return "INR"; }
};

export default function Billing() {
  const { user, refresh } = useAuth();
  const [currency] = useState(detectCurrency);
  const [plans, setPlans] = useState({});
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = async () => {
    const [p, m] = await Promise.all([
      api.get(`/billing/plans?currency=${currency}`),
      api.get("/billing/me"),
    ]);
    setPlans(p.data.plans || {});
    setMe(m.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const introAvailable = me?.intro_available !== false;

  const startTrial = async () => {
    setBusy("trial");
    try {
      await api.post("/billing/start-trial");
      toast.success("Your 3-day free trial is on 🎉");
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start trial");
    } finally {
      setBusy("");
    }
  };

  const ensureSdk = () => {
    if (typeof window === "undefined" || !window.Razorpay) {
      toast.error("Payment SDK not loaded — please refresh and try again.");
      return false;
    }
    return true;
  };

  // Auto-recurring subscription (monthly / yearly).
  const subscribe = async (plan) => {
    if (!ensureSdk()) return;
    setBusy(plan + "-rzp");
    let resp;
    try {
      const r = await api.post("/billing/subscribe", { plan, currency });
      resp = r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription");
      setBusy("");
      return;
    }
    const { subscription_id, key_id, plan: planMeta, prefill } = resp || {};
    if (!key_id || !subscription_id) {
      toast.error("Payments not configured yet. Please contact support.");
      setBusy("");
      return;
    }
    const options = {
      key: key_id,
      subscription_id,
      name: "KAAYA",
      description: `${planMeta?.name || plan} · ${planMeta?.intro || ""} first ${planMeta?.period}, then ${planMeta?.regular}/${planMeta?.period}`,
      prefill: { name: prefill?.name || user?.name || "", email: prefill?.email || user?.email || "" },
      theme: { color: "#FF6B47" },
      modal: { ondismiss: () => setBusy("") },
      handler: async (response) => {
        try {
          await api.post("/billing/verify-subscription", {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Subscription active — welcome aboard 🎉");
          await refresh();
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Verification failed — contact support.");
        } finally {
          setBusy("");
        }
      },
    };
    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setBusy(""); });
      rzp.open();
    } catch (e) {
      toast.error("Could not open payment window");
      setBusy("");
    }
  };

  // One-time order (lifetime).
  const payOneTime = async (plan) => {
    if (!ensureSdk()) return;
    setBusy(plan + "-rzp");
    let resp;
    try {
      const r = await api.post("/billing/checkout", { plan, currency });
      resp = r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setBusy("");
      return;
    }
    const { order, plan: planMeta, key_id, prefill } = resp || {};
    if (!key_id) { toast.error("Payments not configured yet."); setBusy(""); return; }
    const options = {
      key: key_id,
      amount: order.amount,
      currency: order.currency || currency,
      name: "KAAYA",
      description: `${planMeta?.name || plan} plan`,
      order_id: order.id,
      prefill: { name: prefill?.name || user?.name || "", email: prefill?.email || user?.email || "" },
      theme: { color: "#FF6B47" },
      modal: { ondismiss: () => setBusy("") },
      handler: async (response) => {
        try {
          await api.post("/billing/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Payment received — your plan is active 🎉");
          await refresh();
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Payment verification failed — contact support.");
        } finally {
          setBusy("");
        }
      },
    };
    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setBusy(""); });
      rzp.open();
    } catch (e) {
      toast.error("Could not open payment window");
      setBusy("");
    }
  };

  const cancelSubscription = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Auto-renew cancelled. You keep access until your current cycle ends.");
      setCancelOpen(false);
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy("");
    }
  };

  const isActive = !!me?.active;
  const onLifetime = me?.tier_plan === "lifetime";
  const isSubscriber = (me?.tier_plan === "monthly" || me?.tier_plan === "yearly");
  const autoRenewing = isSubscriber && me?.subscription_status === "active";

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="text-overline">plans</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-primary tracking-tight">
          Choose your access
        </h1>
        <p className="text-foreground/70 mt-2 max-w-2xl">
          Start with <strong>3 days free</strong>, then keep going with a plan that fits you. The
          longer your data builds up, the sharper your personalized, evidence-informed guidance gets.
        </p>

        {/* paywall: trial / plan ended nudge */}
        {!isActive && !user?.is_admin && (
          <div className="mt-6 bg-primary/5 border border-primary/30 rounded-3xl p-5 max-w-2xl" data-testid="access-ended-banner">
            <div className="flex items-start gap-3">
              <Lock className="text-primary shrink-0 mt-0.5" size={20} />
              <div className="text-sm">
                <div className="font-medium text-primary" data-testid="access-ended-title">
                  {me?.tier_plan === "trial" ? "Your free trial has ended" : "Your access has ended"}
                </div>
                <div className="text-foreground/75 mt-0.5">
                  Upgrade to continue using your chat, tracker &amp; meal planner — or contact support if you need a hand. Your data is safe and waiting for you.
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 sm:pl-8">
              <Button
                onClick={() => document.getElementById("plans-grid")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="nudge-upgrade-btn"
              >
                <Sparkles size={15} className="mr-1.5" /> Upgrade to continue
              </Button>
              <Button
                onClick={() => setSupportOpen(true)}
                variant="outline"
                className="rounded-full border-border text-foreground/80 hover:bg-muted"
                data-testid="nudge-contact-btn"
              >
                <LifeBuoy size={15} className="mr-1.5" /> Contact support
              </Button>
            </div>
          </div>
        )}

        {/* current status */}
        {isActive && (
          <div className="mt-6 bg-primary/5 border border-primary/20 rounded-3xl p-5 flex flex-wrap items-center gap-3" data-testid="current-tier-card">
            <CheckCircle2 className="text-primary" size={20} />
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium text-primary">
                Active: {me.plan_name || me.tier_plan}
                {autoRenewing && <span className="ml-2 text-xs text-secondary inline-flex items-center gap-1"><RefreshCw size={12} /> auto-renews</span>}
                {isSubscriber && me?.subscription_status && me.subscription_status !== "active" && (
                  <span className="ml-2 text-xs text-muted-foreground">· auto-renew off</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {onLifetime
                  ? "Lifetime access — yours for as long as KAAYA is around"
                  : me.tier_expires_at
                  ? `${me.days_left ?? 0} day(s) left · ${autoRenewing ? "renews" : "until"} ${new Date(me.tier_expires_at).toLocaleDateString()}`
                  : ""}
              </div>
            </div>
            <Badge variant="default" className="rounded-full">ACTIVE</Badge>
            {autoRenewing && (
              <Button size="sm" variant="outline" className="rounded-full border-border text-foreground/70 hover:bg-muted" onClick={() => setCancelOpen(true)} data-testid="cancel-subscription-btn">
                <XCircle size={14} className="mr-1.5" /> Cancel auto-renew
              </Button>
            )}
          </div>
        )}

        <div id="plans-grid" className="mt-10 flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 pb-2 sm:mx-0 sm:px-0 sm:overflow-visible sm:grid sm:grid-cols-2 lg:grid-cols-4">
          {PLAN_ORDER.map((k) => {
            const p = plans[k];
            if (!p) return null;
            const highlighted = k === "monthly";
            const isTrial = k === "trial";
            const isSub = p.kind === "subscription";
            const trialDone = isTrial && me?.trial_used;
            const hasIntro = isSub && introAvailable && !!p.intro_price;
            const bigPrice = isSub ? (hasIntro ? p.intro_price : p.regular_price) : p.price;
            const subline = isTrial
              ? "full access, no card"
              : isSub
              ? (hasIntro
                  ? `first ${p.period}, then ${p.regular_price}/${p.period === "month" ? "mo" : "yr"}`
                  : `${p.regular_price} per ${p.period}, auto-renews`)
              : "one-time payment";
            return (
              <div
                key={k}
                className={`relative snap-center shrink-0 w-[80%] sm:w-auto rounded-3xl border p-5 bg-surface-elevated transition-all duration-300 hover:-translate-y-1 flex flex-col ${
                  highlighted ? "border-secondary shadow-md ring-1 ring-secondary/30" : "border-border"
                }`}
                data-testid={`plan-card-${k}`}
              >
                <div className="text-overline text-secondary min-h-[18px]">{PLAN_BADGE[k]}</div>
                <div className="font-serif-display text-2xl text-primary mt-1">{p.name}</div>
                <div className="font-serif-display text-3xl text-primary mt-2 flex items-baseline gap-1">
                  {bigPrice}
                  {k === "lifetime" && <InfinityIcon size={18} className="text-secondary" />}
                  {isSub && <span className="text-sm text-muted-foreground font-sans">/{p.period === "month" ? "mo" : "yr"}</span>}
                </div>
                <div className="text-xs text-muted-foreground min-h-[16px]">{subline}</div>
                {isSub && introAvailable && !!p.intro_price && (
                  <div className="mt-1.5 inline-flex items-center gap-1 self-start rounded-full bg-secondary/15 px-2.5 py-1 text-[11px] font-semibold text-secondary" data-testid={`intro-badge-${k}`}>
                    <Sparkles size={11} /> Intro price · first {p.period} only
                  </div>
                )}

                {k === "lifetime" && (
                  <div className="mt-2 inline-flex items-start gap-1 self-start rounded-2xl bg-secondary/15 px-2.5 py-1.5 text-[11px] font-semibold leading-snug text-secondary" data-testid="lifetime-limited-badge">
                    <Sparkles size={11} className="mt-0.5 shrink-0" /> Limited access — for serious, health-conscious individuals only!
                  </div>
                )}

                <ul className="mt-4 space-y-2 text-sm flex-1">
                  {(isTrial ? FEATURES.slice(0, 3) : FEATURES).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground/85">{f}</span>
                    </li>
                  ))}
                </ul>

                {isTrial ? (
                  <Button
                    onClick={startTrial}
                    disabled={!!busy || trialDone || isActive}
                    className="w-full mt-5 rounded-full bg-primary hover:bg-primary/90"
                    data-testid={`pay-${k}-btn`}
                  >
                    {busy === "trial" ? "Please wait…" : trialDone ? "Trial used" : "Start 3-day free trial"}
                  </Button>
                ) : (
                  <Button
                    onClick={() => (isSub ? subscribe(k) : payOneTime(k))}
                    disabled={!!busy || onLifetime}
                    className={`w-full mt-5 rounded-full ${
                      highlighted ? "bg-secondary hover:bg-secondary/90" : "bg-primary hover:bg-primary/90"
                    }`}
                    data-testid={`pay-rzp-${k}-btn`}
                  >
                    {busy === k + "-rzp" ? "Opening payment…"
                      : onLifetime ? "Lifetime active"
                      : isSub ? <><RefreshCw size={14} className="mr-1.5" /> Subscribe</>
                      : <><CreditCard size={14} className="mr-1.5" /> Get Lifetime</>}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex items-start gap-2 text-xs text-muted-foreground max-w-2xl mx-auto">
          <Info size={14} className="shrink-0 mt-0.5" />
          <p>
            Monthly &amp; yearly plans <strong>auto-renew</strong> via Razorpay ({currency}) and can be
            cancelled anytime — you keep access until the current cycle ends. The introductory price
            applies to your <strong>first cycle only</strong>. Lifetime is a one-time payment.
            {" "}All payments are non-refundable.
          </p>
        </div>

        <div className="mt-4 text-center">
          <button onClick={() => setSupportOpen(true)} className="text-sm text-secondary hover:underline inline-flex items-center gap-1" data-testid="billing-contact-us">
            <LifeBuoy size={14} /> Trouble paying or have a question? Contact us
          </button>
        </div>
      </div>

      {/* Cancel auto-renew confirm */}
      <ResponsiveModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel auto-renew?"
        description="Your subscription won't renew, but you keep full access until the current cycle ends."
        testid="cancel-subscription-modal"
        className="max-w-md"
      >
        <div className="space-y-3 pb-2">
          <p className="text-sm text-foreground/80">
            You'll keep all features until <strong>{me?.tier_expires_at ? new Date(me.tier_expires_at).toLocaleDateString() : "your cycle ends"}</strong>. You can resubscribe anytime.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setCancelOpen(false)} data-testid="cancel-keep-btn">Keep my plan</Button>
            <Button className="flex-1 rounded-full bg-primary hover:bg-primary/90" disabled={busy === "cancel"} onClick={cancelSubscription} data-testid="cancel-confirm-btn">
              {busy === "cancel" ? "Cancelling…" : "Cancel auto-renew"}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <SupportFeedbackDialog open={supportOpen} onOpenChange={setSupportOpen} defaultType="support" />
    </Layout>
  );
}
